import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface SelectedPropertyState {
  selectedPropertyId: number | null
  setSelectedPropertyId: (id: number | null) => void
  // D3: the persisted pick is scoped to the account it was made under. A
  // fresh session fetch (bootstrap, or a re-login after token expiry with no
  // explicit Logout in between) calls syncAccount with the just-fetched
  // account id: same account keeps the pick, a different account clears
  // selectedPropertyId so it can never leak across accounts. accountId itself
  // is intentionally not exposed as a setter beyond syncAccount, because nothing else
  // should be able to change scope without also deciding what happens to the
  // pick.
  accountId: number | null
  syncAccount: (accountId: number) => void
}

/**
 * Globally-selected property, persisted to localStorage. The header shows
 * a property switcher next to the language toggle; pages that operate on
 * a single property (e.g. /meter-readings) read from this store as the
 * default when no propertyId is in the URL.
 *
 * ── Pages that deliberately IGNORE the global scope ────────────────────────
 *
 * Not every list is property-scopable, and two pages opt out on purpose. If a
 * page below appears to be "missing" property scoping, it is not an oversight —
 * check here before adding it.
 *
 *   /properties  — it IS the property list. Scoping a list of properties to a
 *                  single property is meaningless.
 *
 *   /customers   — a customer only links to a property THROUGH a lease, so any
 *                  property filter has to inner-join leases → unit → room_type →
 *                  property. That silently drops every customer without a lease,
 *                  i.e. every prospect. A prospect having no lease and no
 *                  property link is expected, not missing data, so the filter
 *                  hid real records rather than narrowing them. Removed in
 *                  EP-116 T031 (owner-confirmed 2026-08-06).
 *
 * The trade in both cases is a page not honouring a global control, which reads
 * as an inconsistency. That is preferred over a filter that hides records the
 * user is looking for.
 */
export const useSelectedPropertyStore = create<SelectedPropertyState>()(
  persist(
    (set, get) => ({
      selectedPropertyId: null,
      setSelectedPropertyId: (id) => set({ selectedPropertyId: id }),
      accountId: null,
      syncAccount: (accountId) => {
        if (get().accountId !== accountId) {
          set({ accountId, selectedPropertyId: null })
        } else {
          set({ accountId })
        }
      },
    }),
    { name: 'flowrent-selected-property' }
  )
)
